import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from "react";

export interface ChatMessage {
  id: string;
  author: string;
  body: string;
  timestamp: number;
  /** "system" | "say" | "whisper" | string. */
  channel?: string;
}

export interface ChatProps {
  messages: ChatMessage[];
  onSend?: (text: string) => void;
  /** Max visible width for the panel. */
  width?: number;
  /** Pixel height for the message log. */
  logHeight?: number;
}

const containerStyle: CSSProperties = {
  position: "fixed",
  left: 8,
  bottom: 48,
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 6,
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  fontSize: 12,
  color: "#e6e8ee",
  display: "flex",
  flexDirection: "column",
  zIndex: 999,
};

const logStyle: CSSProperties = {
  padding: "6px 8px",
  overflowY: "auto",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const inputStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  outline: "none",
  color: "#e6e8ee",
  fontSize: 12,
  padding: "6px 8px",
  width: "100%",
  fontFamily: "inherit",
};

const channelColor = (channel?: string): string => {
  switch (channel) {
    case "system":
      return "#facc15";
    case "whisper":
      return "#c084fc";
    case "say":
    default:
      return "#7dd3fc";
  }
};

/**
 * Skeleton chat panel — log + single-line input. Press Enter to focus and
 * submit; Esc blurs. The actual networking is left to the consumer via onSend.
 */
export function Chat({ messages, onSend, width = 320, logHeight = 140 }: ChatProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.code === "Enter" && document.activeElement !== inputRef.current) {
        e.preventDefault();
        inputRef.current?.focus();
      } else if (e.code === "Escape" && document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    onSend?.(trimmed);
    setDraft("");
  };

  const onLocalKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Escape") {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  return (
    <div style={{ ...containerStyle, width }} data-testid="chat-panel">
      <div ref={logRef} style={{ ...logStyle, height: logHeight }}>
        {messages.length === 0 ? (
          <div style={{ opacity: 0.55 }}>(no messages yet — press Enter to type)</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 2 }}>
              <span style={{ color: channelColor(m.channel) }}>{m.author}</span>
              <span style={{ opacity: 0.6 }}>: </span>
              <span>{m.body}</span>
            </div>
          ))
        )}
      </div>
      <form onSubmit={submit}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onLocalKey}
          placeholder="Press Enter to chat…"
          style={inputStyle}
        />
      </form>
    </div>
  );
}
